import {
  Modal,
  Stack,
  Textarea,
  Tabs,
  Button,
  Group,
  ScrollArea,
  ActionIcon,
  Tooltip,
  FocusTrap,
  Badge,
} from "@mantine/core"
import { IconArrowsMaximize } from "@tabler/icons-react"
import { useEffect, useState } from "react"
import Markdown from "react-markdown"
import { useSearchParams } from "react-router-dom"
import rehypeExternalLinks from "rehype-external-links"
import remarkEmoji from "remark-emoji"
import remarkGfm from "remark-gfm"

import { getBadgeColor } from "@costory/shared/utils/digest"

export const CustomMarkdown = ({
  content,
  maxWidth = "500px",
  imgWidth = "400px",
}: {
  content: string
  maxWidth?: string
  imgWidth?: string
}) => {
  return (
    <div style={{ maxWidth, overflow: "auto", margin: "0 auto" }}>
      <Markdown
        rehypePlugins={[[rehypeExternalLinks, { target: "_blank" }], remarkGfm]}
        remarkPlugins={[remarkEmoji]}
        components={{
          img: ({ ...props }) => {
            return <img {...props} style={{ width: imgWidth }} />
          },
          h4: ({ ...props }) => {
            const text = props.children?.toString() || ""
            const parts = text.split(":")
            const badgeType = parts[0]?.trim()
            const badgeText = parts[1]?.trim() || badgeType

            return <Badge color={getBadgeColor(badgeType)}>{badgeText}</Badge>
          },
          a: ({ ...props }) => {
            return (
              <a
                {...props}
                style={{
                  color: "inherit",
                  textDecoration: "underline",
                  fontWeight: "bold",
                }}
                target="_blank"
              />
            )
          },
        }}
      >
        {content}
      </Markdown>
    </div>
  )
}

export const MarkdownEditor = ({
  content,
  setContent,
  isEditingContext,
  toggleIsEditingContext,
  isHovered,
}: {
  content: string
  setContent: (content: string) => void
  isEditingContext: boolean
  toggleIsEditingContext: () => void
  isHovered: boolean
}) => {
  const [currentContent, setCurrentContent] = useState(content)
  useEffect(() => {
    setCurrentContent(content)
  }, [content])
  const [, setSearchParams] = useSearchParams()

  const leaveEditMode = () => {
    setContent(currentContent)
    setSearchParams({ mode: "edit" })
    toggleIsEditingContext()
  }
  const [tabValue, setTabValue] = useState("edit")
  return (
    <>
      <div
        onDoubleClick={toggleIsEditingContext}
        style={{
          minHeight: "30px",
          width: "100%",
          position: "relative",
        }}
      >
        {isHovered && content.length > 0 && (
          <Tooltip label="Preview" position="top">
            <ActionIcon
              onClick={() => {
                setTabValue("preview")
                setSearchParams({ mode: "preview" })

                toggleIsEditingContext()
              }}
              style={{
                position: "absolute",
                top: "4px",
                right: "20px",
                zIndex: 10,
              }}
              size="sm"
              c="textSecondary"
            >
              <IconArrowsMaximize size={20} />
            </ActionIcon>
          </Tooltip>
        )}
        {content.length > 0 && (
          <CustomMarkdown imgWidth="85%" maxWidth="100%" content={content} />
        )}
      </div>
      {isEditingContext && (
        <Modal
          opened={isEditingContext}
          onClose={leaveEditMode}
          centered
          size="90vw"
          title="Contextualize your digest"
          scrollAreaComponent={ScrollArea.Autosize}
          h="90vh"
          styles={{
            body: {
              height: "70vh",
            },
          }}
        >
          <Stack justify="space-between">
            <Tabs
              defaultValue="edit"
              value={tabValue}
              onChange={(tabName) => tabName && setTabValue(tabName)}
            >
              <Tabs.List justify="end">
                <Tabs.Tab value="edit">Edit</Tabs.Tab>
                <Tabs.Tab value="preview">Preview</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="edit">
                <FocusTrap active={isEditingContext && tabValue == "edit"}>
                  <ScrollArea h="50vh">
                    <Textarea
                      style={{
                        marginTop: "5px",
                        marginBottom: "5px",
                        border: "none",
                      }}
                      autosize
                      minRows={5}
                      value={currentContent}
                      onChange={(e) => setCurrentContent(e.target.value)}
                    />
                  </ScrollArea>
                </FocusTrap>
              </Tabs.Panel>

              <Tabs.Panel value="preview">
                <div
                  style={{
                    marginTop: "5px",
                    maxWidth: "95%",
                    overflow: "auto",
                  }}
                >
                  <ScrollArea h="55vh">
                    <CustomMarkdown
                      imgWidth="80%"
                      maxWidth="100%"
                      content={currentContent}
                    />
                  </ScrollArea>
                </div>
              </Tabs.Panel>
            </Tabs>
            <Group justify="flex-end">
              <Button mb="md" onClick={leaveEditMode}>
                Save
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </>
  )
}
